import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { testId } from '../../../../utils/testIds';
import { RENDER_MODE } from '../../constants';

function DecimalField({ config, value, onChange, onBlur, disabled, required, error, renderMode }) {
  const displayValue = value ?? '';

  if (renderMode === RENDER_MODE.CARD || renderMode === RENDER_MODE.GRID) {
    return (
      <span className="field-engine-decimal-value">
        {displayValue !== '' ? formatDecimal(displayValue) : <span className="field-engine-empty">—</span>}
      </span>
    );
  }

  const decimalPlaces = config.fieldSettings?.decimal_places ?? 2;
  const decimalInputPattern = new RegExp(`^\\d*(\\.\\d{0,${decimalPlaces}})?$`);
  const maxValue = config.fieldSettings?.max_value;
  const maxLength = config.maxLength || null;

  const [text, setText] = useState(displayValue === '' ? '' : formatDecimal(displayValue));
  const isFocusedRef = useRef(false);
  const inputRef = useRef(null);
  const pendingCursorRef = useRef(null);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setText(displayValue === '' ? '' : formatDecimal(displayValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayValue]);

  useLayoutEffect(() => {
    if (pendingCursorRef.current != null && inputRef.current) {
      inputRef.current.setSelectionRange(pendingCursorRef.current, pendingCursorRef.current);
      pendingCursorRef.current = null;
    }
  }, [text]);

  return (
    <input
      ref={inputRef}
      id={config.fieldKey}
      data-test={testId('field', config.fieldKey, 'input')}
      name={config.fieldKey}
      type="text"
      inputMode="decimal"
      className={`field-engine-input field-engine-input--decimal${error ? ' field-engine-input--error' : ''}${disabled ? ' disabled' : ''}`}
      value={text}
      onFocus={() => {
        isFocusedRef.current = true;
      }}
      onChange={e => {
        const input = e.target;
        const cursorPos = input.selectionStart ?? input.value.length;
        const digitsBeforeCursor = input.value.slice(0, cursorPos).replace(/,/g, '').length;

        const nextValue = input.value.replace(/,/g, '');
        const previousRawLength = text.replace(/,/g, '').length;
        if (maxLength && nextValue.length > maxLength && nextValue.length > previousRawLength) return;
        if (nextValue === '' || decimalInputPattern.test(nextValue)) {
          const formatted = formatDecimal(nextValue);
          setText(formatted);
          onChange(nextValue);
          pendingCursorRef.current = cursorPosForDigitCount(formatted, digitsBeforeCursor);
        }
      }}
      onBlur={e => {
        isFocusedRef.current = false;
        const raw = text.replace(/,/g, '');
        const finalValue = cleanupDecimal(raw, maxValue);
        onChange(finalValue);
        setText(finalValue === '' ? '' : formatDecimal(finalValue));
        onBlur(e);
      }}
      disabled={disabled}
      required={required}
      aria-required={required}
      aria-invalid={!!error}
      placeholder={config.fieldSettings?.placeholder ?? config.label ?? ''}
    />
  );
}

function cursorPosForDigitCount(formatted, digitCount) {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] !== ',') {
      seen++;
      if (seen === digitCount) return i + 1;
    }
  }
  return formatted.length;
}

function cleanupDecimal(rawDigits, maxValue) {
  if (rawDigits === '' || rawDigits === '.') return '';

  const hasDot = rawDigits.includes('.');
  let [intPart, fracPart] = rawDigits.split('.');
  intPart = intPart.replace(/^0+(?=\d)/, '') || '0';

  let result = (hasDot && fracPart !== '') ? `${intPart}.${fracPart}` : intPart;

  if (typeof maxValue === 'number' && Number.isSafeInteger(maxValue) && intPart.length <= 15) {
    const numVal = Number(result);
    if (Number.isFinite(numVal) && numVal > maxValue) {
      result = String(maxValue);
    }
  }

  return result;
}

function formatDecimal(value) {
  const str = String(value).replace(/,/g, '');
  const [intPart, fracPart] = str.split('.');
  if (!/^\d+$/.test(intPart || '')) return str;
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fracPart !== undefined ? `${grouped}.${fracPart}` : grouped;
}

export default DecimalField;
